import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileDown, Search, ShieldCheck } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import JournalMCP from "@/components/JournalMCP";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ACTEURS = [
  { key: "", label: "Tous les acteurs" },
  { key: "client", label: "Clients" },
  { key: "admin", label: "Équipe BKPRO" },
  { key: "partage", label: "Liens partagés" },
];
const ACTIONS = [
  { key: "", label: "Toutes les actions" },
  { key: "preview", label: "Aperçus" },
  { key: "download", label: "Téléchargements" },
];
const ACTEUR_STYLE = {
  client: "bg-blue-50 text-blue-700 border-blue-200",
  admin: "bg-slate-100 text-slate-700 border-slate-200",
  partage: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AdminJournal() {
  const [logs, setLogs] = useState([]);
  const [filtres, setFiltres] = useState({ q: "", acteur_type: "", action: "", jours: 90 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filtres).filter(([, v]) => v !== "" && v !== null));
    api.get("/admin/documents/access-logs", { params }).then(({ data }) => {
      setLogs(data);
      setLoading(false);
    });
  }, [filtres]);

  useEffect(() => {
    load();
  }, [load]);

  const exporter = async () => {
    const { data } = await api.get("/admin/documents/access-logs/export", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "journal-acces-coffre-fort.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 20000);
  };

  return (
    <div className="space-y-6" data-testid="admin-journal">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="bk-label mb-2">Coffre-fort</div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Journal des accès</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Traçabilité complète : qui a ouvert ou téléchargé quel document, quand, depuis quelle adresse IP. Conservation 12 mois glissants.
          </p>
        </div>
        <Button data-testid="journal-export-btn" onClick={exporter} variant="outline">
          <FileDown size={15} className="mr-2" /> Export CSV
        </Button>
      </div>

      <JournalMCP />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="flex gap-2 sm:col-span-2">
          <Input
            data-testid="journal-search-input"
            value={filtres.q}
            onChange={(e) => setFiltres({ ...filtres, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher (document, acteur, lien)"
            className="h-11"
          />
          <Button data-testid="journal-search-btn" onClick={load} className="h-11 bg-slate-900 hover:bg-slate-700">
            <Search size={15} />
          </Button>
        </div>
        <select
          data-testid="journal-acteur-select"
          value={filtres.acteur_type}
          onChange={(e) => setFiltres({ ...filtres, acteur_type: e.target.value })}
          className="h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white"
        >
          {ACTEURS.map((a) => (
            <option key={a.key} value={a.key}>{a.label}</option>
          ))}
        </select>
        <select
          data-testid="journal-action-select"
          value={filtres.action}
          onChange={(e) => setFiltres({ ...filtres, action: e.target.value })}
          className="h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white"
        >
          {ACTIONS.map((a) => (
            <option key={a.key} value={a.key}>{a.label}</option>
          ))}
        </select>
        <select
          data-testid="journal-periode-select"
          value={filtres.jours}
          onChange={(e) => setFiltres({ ...filtres, jours: Number(e.target.value) })}
          className="h-11 border border-slate-200 rounded-lg px-3 text-sm bg-white"
        >
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
          <option value={365}>12 derniers mois</option>
        </select>
      </div>

      {loading && <div className="text-sm text-slate-400">Chargement…</div>}

      {!loading && logs.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="journal-empty">
          <ShieldCheck size={26} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucun accès enregistré sur cette période.</p>
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="journal-table">
          <div className="hidden lg:grid grid-cols-[150px_100px_1fr_1fr_130px] gap-3 px-4 py-3 bk-label border-b border-slate-100">
            <span>Date</span>
            <span>Action</span>
            <span>Acteur</span>
            <span>Document</span>
            <span>IP</span>
          </div>
          {logs.map((l) => (
            <div key={l.id} data-testid={`journal-row-${l.id}`} className="grid lg:grid-cols-[150px_100px_1fr_1fr_130px] gap-1 lg:gap-3 px-4 py-3 border-b border-slate-100 last:border-0 text-sm hover:bg-slate-50/70 transition-colors">
              <span className="text-xs text-slate-500">{fmtDate(l.created_at)}</span>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                {l.action === "preview" ? <Eye size={13} className="text-[#005CFF]" /> : <Download size={13} className="text-slate-400" />}
                {l.action === "preview" ? "Aperçu" : "Téléch."}
              </span>
              <span className="min-w-0">
                <span className={`inline-block text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 mr-2 ${ACTEUR_STYLE[l.acteur_type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                  {l.acteur_type}
                </span>
                <span className="text-slate-800 break-all">{l.acteur}</span>
                {l.share_libelle && <span className="block text-xs text-slate-500 truncate">via {l.share_libelle}</span>}
              </span>
              <span className="min-w-0">
                <span className="block text-slate-900 font-medium truncate">{l.document_titre}</span>
                <span className="block text-xs text-slate-500 truncate">{l.site_nom || "tous sites"} · {l.categorie}</span>
              </span>
              <span className="text-xs text-slate-500 break-all">{l.ip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
